import { api } from "roboflex-thalamus-request-handler";
import { ProductionOrder } from "../model/ProductionOrder";

export default {
  async getOPs(page = 1, concluida = false, dtInicio = null, q = "") {
    const payload = { page, concluida };
    if (dtInicio) payload.dtInicio = dtInicio;
    if (q && q.trim() !== "") payload.q = q.trim();

    const response = await api.post("ordem-producao/listar", payload);

    return {
      ops: response.data.data.map((e) => ProductionOrder.fromJson(e)),
      pagination: {
        current: response.data.current_page,
        last: response.data.last_page,
      },
    };
  },

  async getOpByNum(opNum) {
    try {
      const response = await api.get("/producao-by-num", {
        params: { opNum },
      });
      return response.data;
    } catch (error) {
      throw error.response?.data?.msg || "Erro ao buscar OP por número.";
    }
  },

  async estruturaProdutoEspecificaOP(opNum) {
    const resp = await api.get("producao-produto-estrutura", { params: { opNum } });
    const lista = Array.isArray(resp?.data?.data) ? resp.data.data : [];

    return lista.map((e) => ({
      id: e?.id ?? e?.produto?.produto_cod ?? e?.produto_cod,
      code: e?.produto?.cod ?? "",
      name: e?.produto?.desc ?? "",
      unity: e?.produto?.und ?? "",
      quantity: Number(e?.qtd),
      separatedQuantity: Number(e?.qtd_separada),
      invoice: e?.notaFiscal ?? "",
      type: e?.produto?.tipo ? { name: e.produto.tipo.nome } : undefined,
      family: e?.produto?.familia_produto?.familia_nome ?? "-",
      _raw: e,
    }));
  },

    async createOp(op) {
    try {
      const payload = op.toJson();
      const response = await api.post("omie/op/cadastrar", payload);
      return response.data;
    } catch (error) {
      console.error("Erro ao criar OP", error.response?.data || error.message);
      throw error;
    }
  },

    async updateOp({ opNum, dados }) {
    try {
      return await api.patch(`omie/op/atualizar`, {
        opNum,
        ...dados,
      });
    } catch (error) {
      console.error("Erro ao atualizar OP", error.response?.data || error.message);
      throw error;
    }
  },

    async deleteOp({ opNum, confirm }) {
    try {
      return await api.post(`omie/op/excluir`, {
        opNum,
        confirm,
      });
    } catch (error) {
      console.error("Erro ao excluir OP", error.response?.data || error.message);
      throw error;
    }
  },

  /**
   * Conclui a ordem de produção no Omie.
   * @param {{ op_cod: number, dDtConclusao: string, nQtdeProduzida: number, cObsConclusao?: string }} payload
   */
  async concluirOpOmie(payload) {
    const response = await api.post("crm-producao/op/conclui", payload);
    return response.data;
  },

  /**
   * Inicia apontamento para uma OP (apontamento "iniciado").
   * @param {string|number} opIdentifier - opNum ou op_cod da OP
   */
  async iniciarApontamento(opIdentifier) {
    const response = await api.post("producao/apontamento", {
      op: String(opIdentifier),
      apontamento: "iniciado",
    });
    return response.data;
  },

  async getOpsAtivas() {
    const resp = await api.post("/producao/apontamento/op/ativas");
    return resp.data?.ops_ativas ?? [];
  },

  // adição de produtos
  async getEstoquesByProdutos(lista_produtos) {
    try {
      const { data } = await api.post("omie/estoque/buscar", { lista_produtos });
      return data;
    } catch (error) {
      console.error("Erro ao buscar estoque", error.response?.data || error.message);
      throw error;
    }
  },

  // criação op
  async getEstoquesByProdutoRaiz(produto_cod) {
    const { data } = await api.post("omie/estoque/buscar", { produto_cod });
    return data;
  },

  // edição de op
  async getEstoquesByOP(opNum) {
    try {
      const { data } = await api.post("omie/estoque/buscar", { opNum });
      return data;
    } catch (error) {
      console.error("Erro ao buscar estoque", error.response?.data || error.message);
      throw error;
    }
  },

  // Anexos
  async getAnexos(opNum) {
    try {
      const response = await api.get(`anexos/producao/${opNum}`);
      // Handle different response structures
      let anexos = response.data;
      // If response.data is an array, use it directly
      if (Array.isArray(anexos)) {
        return anexos;
      }
      // If response.data has a data property with array, use that
      if (Array.isArray(anexos?.data)) {
        return anexos.data;
      }
      // If response itself is an array (unlikely but possible)
      if (Array.isArray(response)) {
        return response;
      }
      console.warn("Unexpected response structure for anexos:", response);
      return [];
    } catch (error) {
      console.error("Erro ao buscar anexos", error.response?.data || error.message);
      throw error;
    }
  },

  async uploadAnexos(opNum, files) {
    try {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append("arquivo[]", file);
      });

      const response = await api.post(`anexar/producao/${opNum}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      console.error("Erro ao enviar anexos", error.response?.data || error.message);
      throw error;
    }
  },

  async deleteAnexo(opNum, anexoId) {
    try {
      const response = await api.delete(`anexos/producao/${opNum}/${anexoId}`);
      return response.data;
    } catch (error) {
      console.error("Erro ao excluir anexo", error.response?.data || error.message);
      throw error;
    }
  },

  downloadAnexo(anexoId) {
    const url = `${process.env.VUE_APP_ROOT_API}/anexo/download/${anexoId}`;
    window.location.href = url;
  },

  getAnexoUrl(anexo) {
    const baseUrl = process.env.VUE_APP_ROOT_STORAGE;
    return `${baseUrl}/${anexo.caminho || anexo.path}`;
  },

  /**
   * Lista arquivos do produto marcados para incluir na OP (gestão de arquivos).
   * @param {string} produtoCod - código do produto (produto_cod)
   * @returns {Promise<{ nome: string, versao: string }[]>}
   */
  async getArquivosIncluirNaOp(produtoCod) {
    try {
      const { data } = await api.get(`/gestao-arquivos/produto/${encodeURIComponent(produtoCod)}/arquivos-incluir-na-op`);
      return Array.isArray(data?.arquivos) ? data.arquivos : [];
    } catch (err) {
      console.warn("Erro ao buscar arquivos do produto para OP:", err?.response?.data || err.message);
      return [];
    }
  },
};
